import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output,
  ViewChild
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { uniq, uniqBy } from 'lodash-es';
import {
  catchError, combineAll, debounceTime, filter, from as observableFrom, map, Observable, of,
  switchMap, takeUntil
} from 'rxjs';

import { GetUserByIdGQL, SearchUserQueryGQL, SearchUserQueryQuery } from 'generated/graphql';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';


export type SearchUserQueryUser = SearchUserQueryQuery['searchUsers']['edges'][0]['node'];
@Component({
  selector: 'app-user-list-autocomplete',
  templateUrl: './user-list-autocomplete.component.html',
  styleUrls: ['./user-list-autocomplete.component.scss']
})

export class UserListAutocompleteComponent
  extends WithDisposableSubject() implements OnInit, OnDestroy {

  private _loading = false;

  get loading() {
    return this._loading;
  }

  set loading(value: boolean) {
    this._loading = value;

    if (value) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }


  @Input() userIds: string[] = [];

  @Input() label = 'Search Users';
  /** Subset of userIds that can't be removed */
  @Input() requiredUserIds: string[] = [];

  @Output() userIdsChange = new EventEmitter<string[]>();

  /** Users shown in the pills */
  selectedUsers: { id: string; firstName: string; lastName: string; email: string }[] = [];

  @ViewChild('searchInputElement', { static: true }) searchInputElement: ElementRef;

  /** Form control for searching */
  searchControl = new UntypedFormControl();

  /** Results from search */
  filteredResults$: Observable<{ id: string; firstName: string; lastName: string; email: string }[]>;

  @Input() postFilterFn: (user: SearchUserQueryUser) => boolean = () => true;
  /** Optional: campaign id */
  @Input() campaignId: string;

  /** Disabled state of the input */
  @Input() disabled: boolean;

  constructor(
    private _cd: ChangeDetectorRef,
    private _searchUsersQueryGQL: SearchUserQueryGQL,
    private _getUserByIdGQL: GetUserByIdGQL
  ) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this._loadUsersFromIds(this.userIds);
    this._setupFilteredResults();
    if (this.disabled) {
      this.searchControl.disable();
    }
  }

  onSelected(event: MatAutocompleteSelectedEvent) {
    this.selectedUsers.push(event.option.value);
    this.userIds = this.selectedUsers?.map(p => p.id);
    this.userIdsChange.emit(this.userIds);
    this.searchControl.setValue('', { emitEvent: false, emitModelToViewChange: true });
    // Set the value to blank after selection
    setTimeout(() => this.searchInputElement.nativeElement.value = '');
  }

  onRemoved(user: { id: string }) {
    this.selectedUsers = this.selectedUsers.filter(p => p.id !== user.id);
    this.userIds = this.selectedUsers.map(p => p.id);
    this.userIdsChange.emit(this.userIds);
  }

  /**
   * Display function for autocomplete
   * which always displays an empty string,
   * because the selected values are shown as mat-chips
   *
   * @param _value
   */
  displayWithFn(_value: any) {
    return '';
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cd.detectChanges();
  }

  private _setupFilteredResults() {
    this.filteredResults$ = this.searchControl
      .valueChanges.pipe(
        takeUntil(this.disposer),
        filter((val) => (val || '').length > 2),
        debounceTime(200),
        switchMap((keyword) =>
          this._searchUsersQueryGQL.fetch(
            { keyword, campaignId: this.campaignId || undefined },
            { fetchPolicy: 'network-only' }
          )
        ),
        filter(({ data }) => !!data),
        map(r => {
          const users = r.data?.searchUsers.edges.map(p => p.node)
            .filter(user => !(this.userIds || []).includes(user.id));
          return users;
        }),
        map(users => users.filter(this.postFilterFn)),
      );
  }

  private _loadUsersFromIds(ids: string[]) {
    if (!ids || !ids.length) {
      this.loading = false;
      return;
    }

    observableFrom(ids).pipe(
      map(id =>
        this._getUserByIdGQL.fetch({ id }).pipe(
          catchError(e => of(null)),
          map(res => res ? res.data.getUserById : null)
        )
      ),
      combineAll()
    ).subscribe({
      next: (results) => {
        const users = results.filter(r => !!r);
        this.selectedUsers = uniqBy([...this.selectedUsers, ...users], 'id');
        this.userIds = uniq(this.selectedUsers.map(p => p.id));
      },
      error: (e) => console.error(e),
      complete: () => {
        this.loading = false;
        this.handleDataChange(this.userIds);
      }
    });
  }

  // ----------END----------

  private handleDataChange(userIds: string[]): void {
    this._cd.markForCheck();
    this.userIdsChange.emit(userIds);
  }

}
